<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1000px"
    scrollable
    persistent
  >
    <v-card class="formats-modal-card">
      <!-- Modal Header -->
      <v-card-title class="grey lighten-3 py-3 px-4 d-flex align-center justify-space-between border-bottom">
        <div class="d-flex align-center">
          <v-icon color="deep-purple darken-1" class="mr-2">mdi-file-certificate-outline</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold text--primary">
              Formatos y Anexos a Firmar del Contrato #{{ contract ? contract.AgtID : '' }}
            </div>
            <div class="caption text--secondary" v-if="contract">
              <strong>Colaborador:</strong> {{ contract.NtpFullName }} | <strong>DNI:</strong> {{ contract.PrsDocumentNumber }}
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <v-btn
            color="primary"
            small
            outlined
            class="mr-2 text-none"
            @click="printCurrentFormat"
            :disabled="loading || dynamicFormats.length === 0"
          >
            <v-icon small left>mdi-printer</v-icon>
            Imprimir {{ isShowingAll ? 'Todos' : 'Formato' }}
          </v-btn>

          <v-btn icon small @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- Selector de Formatos / Tabs -->
      <div class="bg-light-purple px-4 pt-2 border-bottom" v-if="dynamicFormats.length > 0">
        <v-tabs
          v-model="activeTab"
          color="deep-purple darken-2"
          active-class="font-weight-bold"
          show-arrows
          dense
        >
          <v-tab v-for="(fmt, idx) in tabList" :key="idx" class="text-none caption">
            <v-icon x-small left>{{ fmt.icon }}</v-icon>
            {{ fmt.title }}
          </v-tab>
        </v-tabs>
      </div>

      <!-- Modal Content Body -->
      <v-card-text class="pa-4 bg-grey-lighten-4 printable-area">
        <!-- Loader -->
        <div v-if="loading" class="text-center py-10">
          <v-progress-circular indeterminate color="deep-purple" size="44"></v-progress-circular>
          <div class="caption text--secondary mt-2">Cargando datos del colaborador y formatos...</div>
        </div>

        <!-- Sin formatos configurados -->
        <div v-else-if="dynamicFormats.length === 0" class="text-center py-10">
          <v-icon size="48" color="grey lighten-1">mdi-file-hidden</v-icon>
          <div class="text-subtitle-2 grey--text text--darken-1 mt-2">
            No hay formatos ni entregables configurados para el puesto de este contrato.
          </div>
          <div class="caption grey--text">
            Configure los documentos en el módulo "Documentos por Puesto" para que aparezcan aquí.
          </div>
        </div>

        <div v-else>
          <!-- Indicador de Navegación -->
          <div class="d-flex align-center justify-space-between mb-3 px-2 no-print">
            <span class="caption text--secondary font-weight-medium">
              Formato {{ activeTab + 1 }} de {{ tabList.length }}:
              <strong class="deep-purple--text text--darken-2">{{ currentTabInfo.title }}</strong>
            </span>

            <div class="d-flex align-center gap-2">
              <v-btn
                x-small
                outlined
                color="indigo"
                :disabled="activeTab === 0"
                @click="activeTab--"
              >
                <v-icon x-small left>mdi-chevron-left</v-icon> Anterior
              </v-btn>

              <v-btn
                x-small
                color="indigo darken-1"
                dark
                :disabled="activeTab === tabList.length - 1"
                @click="activeTab++"
              >
                Siguiente <v-icon x-small right>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- RENDERIZADO DINÁMICO DE FORMATOS -->
          <div ref="printContainer" class="format-render-wrapper">
            <template v-for="(fmt, idx) in dynamicFormats">
              <div
                v-if="activeTab === idx || isShowingAll"
                :key="'fmt-' + idx"
                class="format-page-item mb-6"
              >
                <!-- Componente Vue dinámico -->
                <component
                  v-if="fmt.PdcComponentName && componentMap[fmt.PdcComponentName]"
                  :is="componentMap[fmt.PdcComponentName]"
                  :worker-data="workerData"
                  :contract-data="contract"
                  :date-value="contractDate"
                />

                <!-- Solo entregable PDF (sin componente Vue) -->
                <div v-else-if="fmt.PdcFileName && !fmt.PdcComponentName" class="text-center py-6">
                  <v-icon size="48" color="red darken-1">mdi-file-pdf-box</v-icon>
                  <div class="text-subtitle-2 mt-2">{{ fmt.PdcDocumentTitle || fmt.PdcFileName }}</div>
                  <div class="caption grey--text mb-3">Documento entregable PDF</div>
                  <v-btn
                    small
                    color="red darken-1"
                    dark
                    class="text-none"
                    @click="downloadEntregable(fmt)"
                  >
                    <v-icon small left>mdi-download</v-icon>
                    Descargar PDF
                  </v-btn>
                </div>

                <!-- Componente no encontrado -->
                <div v-else class="text-center py-6">
                  <v-icon size="48" color="orange darken-1">mdi-alert-circle-outline</v-icon>
                  <div class="text-subtitle-2 mt-2">Componente "{{ fmt.PdcComponentName }}" no encontrado</div>
                  <div class="caption grey--text">Verifique que el nombre del componente sea correcto.</div>
                </div>

                <!-- Botón de descarga del entregable PDF cuando hay tanto componente como PDF -->
                <div v-if="fmt.PdcComponentName && fmt.PdcFileName" class="text-center mt-3 no-print">
                  <v-btn
                    x-small
                    outlined
                    color="red darken-1"
                    class="text-none"
                    @click="downloadEntregable(fmt)"
                  >
                    <v-icon x-small left>mdi-file-pdf-box</v-icon>
                    Descargar Entregable: {{ fmt.PdcFileName }}
                  </v-btn>
                </div>
              </div>
            </template>
          </div>
        </div>
      </v-card-text>

      <!-- Modal Footer -->
      <v-card-actions class="grey lighten-3 px-4 py-2 border-top no-print">
        <div class="caption text--secondary">
          <v-icon x-small color="info" class="mr-1">mdi-information-outline</v-icon>
          Cada formato requiere firma hológrafa y huella dactilar del colaborador al momento de contratar.
        </div>
        <v-spacer></v-spacer>
        <v-btn text small color="grey darken-2" @click="closeModal">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _sCttoService from "@/services/HumanResource/HmnContractService.js";
import _sPositionDocument from "@/services/HumanResource/HmnPositionDocumentService.js";
import FormatRisst from "./Formats/FormatRisst.vue";
import FormatRit from "./Formats/FormatRit.vue";
import FormatCodeOfConduct from "./Formats/FormatCodeOfConduct.vue";
import FormatAddressDeclaration from "./Formats/FormatAddressDeclaration.vue";
import FormatDiningDeduction from "./Formats/FormatDiningDeduction.vue";
import FormatBackgroundDeclaration from "./Formats/FormatBackgroundDeclaration.vue";
import FormatSubstanceDeclaration from "./Formats/FormatSubstanceDeclaration.vue";
import FormatOvertimeCompensation from "./Formats/FormatOvertimeCompensation.vue";
import FormatDigitalConsentAri from "./Formats/FormatDigitalConsentAri.vue";
import FormatDigitalConsentAriDeliverable from "./Formats/FormatDigitalConsentAriDeliverable.vue";
import FormatSSTCritical from "./Formats/FormatSSTCritical.vue";

export default {
  name: "ContractFormatsModal",

  components: {
    FormatRisst,
    FormatRit,
    FormatCodeOfConduct,
    FormatAddressDeclaration,
    FormatDiningDeduction,
    FormatBackgroundDeclaration,
    FormatSubstanceDeclaration,
    FormatOvertimeCompensation,
    FormatDigitalConsentAri,
    FormatDigitalConsentAriDeliverable,
    FormatSSTCritical,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    contract: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      activeTab: 0,
      loading: false,
      workerData: {},
      dynamicFormats: [],
      // Mapeo de nombre de componente a componente registrado
      componentMap: {
        FormatRisst: FormatRisst,
        FormatRit: FormatRit,
        FormatCodeOfConduct: FormatCodeOfConduct,
        FormatAddressDeclaration: FormatAddressDeclaration,
        FormatDiningDeduction: FormatDiningDeduction,
        FormatBackgroundDeclaration: FormatBackgroundDeclaration,
        FormatSubstanceDeclaration: FormatSubstanceDeclaration,
        FormatOvertimeCompensation: FormatOvertimeCompensation,
        FormatDigitalConsentAri: FormatDigitalConsentAri,
        FormatDigitalConsentAriDeliverable: FormatDigitalConsentAriDeliverable,
        FormatSSTCritical: FormatSSTCritical,
      },
      // Iconos por nombre de componente
      iconMap: {
        FormatRisst: "mdi-shield-check",
        FormatRit: "mdi-book-open-page-variant",
        FormatCodeOfConduct: "mdi-file-certificate",
        FormatAddressDeclaration: "mdi-home-city",
        FormatDiningDeduction: "mdi-silverware-fork-knife",
        FormatBackgroundDeclaration: "mdi-shield-account",
        FormatSubstanceDeclaration: "mdi-pill",
        FormatOvertimeCompensation: "mdi-clock-time-four-outline",
        FormatDigitalConsentAri: "mdi-file-sign",
        FormatDigitalConsentAriDeliverable: "mdi-qrcode",
        FormatSSTCritical: "mdi-alert-decagram",
      },
    };
  },

  computed: {
    tabList() {
      const tabs = this.dynamicFormats.map((fmt, idx) => ({
        title: `${idx + 1}. ${fmt.PdcDocumentTitle || fmt.PdcComponentName || fmt.PdcFileName || 'Documento'}`,
        icon: this.iconMap[fmt.PdcComponentName] || (fmt.PdcFileName ? "mdi-file-pdf-box" : "mdi-file-document-outline"),
      }));
      // Pestaña "Todos" al final si hay más de 1 formato
      if (tabs.length > 1) {
        tabs.push({ title: "📑 Todos", icon: "mdi-file-multiple" });
      }
      return tabs;
    },
    currentTabInfo() {
      return this.tabList[this.activeTab] || {};
    },
    isShowingAll() {
      return this.dynamicFormats.length > 1 && this.activeTab === this.dynamicFormats.length;
    },
    contractDate() {
      if (this.contract && this.contract.AgtBeginDate) {
        return this.contract.AgtBeginDate;
      }
      return new Date();
    },
  },

  watch: {
    value(val) {
      if (val && this.contract) {
        this.loadAll();
      }
    },
    contract(newVal) {
      if (this.value && newVal) {
        this.loadAll();
      }
    },
  },

  methods: {
    async loadAll() {
      if (!this.contract) return;
      this.loading = true;
      this.activeTab = 0;
      this.dynamicFormats = [];

      try {
        // Cargar datos completos del trabajador y formatos en paralelo
        const [workerRes, formatsRes] = await Promise.all([
          this.fetchFullWorkerData(),
          this.fetchFormatsByPosition(),
        ]);

        if (workerRes) this.workerData = workerRes;
        if (formatsRes) this.dynamicFormats = formatsRes;
      } catch (err) {
        console.warn("Error al cargar datos del modal de formatos:", err);
      } finally {
        this.loading = false;
      }
    },

    async fetchFullWorkerData() {
      try {
        const agtId = this.contract.AgtID;
        const userId = this.$fun ? this.$fun.getUserID() : 1;
        
        const res = await _sCttoService.searchContract(agtId, userId);
        if (res && res.data && res.data[0]) {
          return res.data[0];
        }
        return { ...this.contract };
      } catch (err) {
        console.warn("Error al cargar datos detallados del trabajador:", err);
        return { ...this.contract };
      }
    },

    async fetchFormatsByPosition() {
      try {
        const agtId = this.contract.AgtID || null;
        const res = await _sPositionDocument.getByPosition(pstId, userId, agtId);
        if (res && res.data && Array.isArray(res.data)) {
          return res.data;
        }
        return [];
      } catch (err) {
        console.warn("Error al cargar formatos por puesto:", err);
        return [];
      }
    },

    downloadEntregable(fmt) {
      if (!fmt.PdcFileName) return;
      // Construir URL de descarga del archivo PDF
      const baseUrl = this.$const ? this.$const.URL_DOCUMENTS_POSITION_RRHH || "" : "";
      const url = `${baseUrl}${fmt.PdcFileName}`;
      window.open(url, "_blank");
    },

    closeModal() {
      this.$emit("input", false);
    },

    printCurrentFormat() {
      window.print();
    },
  },
};
</script>

<style scoped>
.formats-modal-card {
  border-radius: 8px;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.border-top {
  border-top: 1px solid #e0e0e0;
}

.bg-light-purple {
  background-color: #f5f0fb;
}

.bg-grey-lighten-4 {
  background-color: #f8f9fa;
}

.gap-2 {
  gap: 8px;
}

.line-height-tight {
  line-height: 1.2;
}

.format-page-item {
  page-break-after: always;
}

/* Reglas de Impresión CSS */
@media print {
  body * {
    visibility: hidden;
  }
  .printable-area, .printable-area * {
    visibility: visible;
  }
  .printable-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    background-color: #fff !important;
  }
  .no-print {
    display: none !important;
  }
  .format-page-item {
    page-break-after: always !important;
    box-shadow: none !important;
    border: none !important;
  }
}
</style>
